import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useMsal } from "@azure/msal-react";
import { Spinner, Alert, Button, Modal, Form } from "react-bootstrap";
import { createGroup, getGroupCreateDefaults, getMyResources, getGroupByGraphId, updateSiteSavedDataByGraphId } from "@/services/resourcesApi";
import { usePageTitle } from "@/hooks/usePageTitle";
import { ResourceSummary, ResourceType, TeamInfo } from "@/types";
import ResourceDetailsPanel from "./ResourceDetailsPanel";
import ServiceIcons from "./ServiceIcons";
import DeleteGroupModal from "./DeleteGroupModal";
import SiteSavedDataModal from "./SiteSavedDataModal";
import ConfirmModal from "@/components/ConfirmModal";
import ErrorModal from "@/components/ErrorModal";
import SuccessModal from "@/components/SuccessModal";
import BulkUpdateMetadataModal from "./BulkUpdateMetadataModal";
import { acquireTokenForApi } from "@/authConfig";
import "@/pages/Resources/Resources.scss";

import {
  FaUsers,
  FaComments,
  FaGlobe,
  FaHashtag,
  FaCloud,
  FaUser,
  FaBox,
  FaUserMinus,
  FaTrash,
  FaTimes,
  FaExclamationTriangle,
  FaPlus,
  FaSyncAlt,
  FaCheckCircle,
  FaPen,
} from "react-icons/fa";

interface TabInfo {
  id: string;         // internalResourceId OR "resources" OR graphId (for direct access)
  title: string;      // display name
  isGraphId?: boolean;  // indicates this tab loads from Graph API directly
}

const MAX_BULK_SELECTIONS = 10;

interface ResourceMetadata {
  primaryOwnerNtid?: string;
  secondaryOwnerNtid?: string;
  primaryAdminNtid?: string;
  secondaryAdminNtid?: string;
  costCenter?: string;
  businessUnit?: string;
  codeOfConductSensitive?: boolean;
  lastUpdated?: string;
}

type MetadataStatusFilter = "all" | "needs-information" | "up-to-date";
type OwnershipFilter = "all" | "owned" | "member";

const ResourcesPage: React.FC = () => {
  const { graphId } = useParams<{ graphId?: string }>();
  const { accounts } = useMsal();
  const [resources, setResources] = useState<ResourceSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [search, setSearch] = useState("");
  const [ownershipFilter, setOwnershipFilter] = useState<OwnershipFilter>("all");
  const [metadataStatusFilter, setMetadataStatusFilter] = useState<MetadataStatusFilter>("all");
  const [showCreateGroupModal, setShowCreateGroupModal] = useState(false);
  const [metadataResource, setMetadataResource] = useState<ResourceSummary | null>(null);
  const [loadingCreateDefaults, setLoadingCreateDefaults] = useState(false);
  const [creatingGroup, setCreatingGroup] = useState(false);
  const [createGroupError, setCreateGroupError] = useState<string | null>(null);
  const [createGroupForm, setCreateGroupForm] = useState({
    title: "",
    description: "",
    primaryOwnerEmail: "",
    secondaryOwnerEmail: "",
    businessUnit: "",
    costCenter: "",
  });
  
  // Bulk update state
  const [selectedResourceIds, setSelectedResourceIds] = useState<Set<string>>(new Set());
  const [showBulkUpdateModal, setShowBulkUpdateModal] = useState(false);
  const [updatingMetadata, setUpdatingMetadata] = useState(false);

  // Quick actions state
  const [removingGroupId, setRemovingGroupId] = useState<string | null>(null);
  const [showConfirmRemove, setShowConfirmRemove] = useState(false);
  const [groupToRemove, setGroupToRemove] = useState<ResourceSummary | null>(null);
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [groupToDelete, setGroupToDelete] = useState<ResourceSummary | null>(null);
  const [deletingGroupId, setDeletingGroupId] = useState<string | null>(null);
  const [errorModal, setErrorModal] = useState({
    show: false,
    title: "",
    message: "",
  });

  const [successModal, setSuccessModal] = useState({
    show: false,
    title: "",
    message: "",
  });

  // Compliance summary state
  const showError = (title: string, message: string) => {
    setErrorModal({ show: true, title, message });
  };

  const showSuccess = (title: string, message: string) => {
    setSuccessModal({ show: true, title, message });
  };

  // ---- Tabs ----

  // Always have “Resources” tab open by default
  const [openTabs, setOpenTabs] = useState<TabInfo[]>([
    { id: "resources", title: "All groups" }
  ]);

  // Active tab initially = resources
  const [activeTabId, setActiveTabId] = useState("resources");
  const [loadingTabId, setLoadingTabId] = useState<string | null>(null);
  const activeTabTitle = openTabs.find((tab) => tab.id === activeTabId)?.title;

  usePageTitle(activeTabId === "resources" ? null : activeTabTitle, "Resources");

  const getResourceTypeIcon = (type: ResourceType | string) => {
    const t = (type || "").toLowerCase();

    if (t === "group") return <FaUsers />;
    if (t === "team") return <FaComments />;
    if (t === "site") return <FaGlobe />;
    if (t === "channel") return <FaHashtag />;
    if (t === "onedrive") return <FaCloud />;
    if (t === "user") return <FaUser />;

    return <FaBox />;
  };

  // ---- Load My Resources ----

  const loadResources = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getMyResources();

      // Deduplicate logic (owner + member)
      const map = new Map<string, ResourceSummary>();
      for (const r of data) {
        const key = r.internalResourceId;
        // Map userRole from backend (camelCase from JSON serializer) to isOwner/isMember flags
        const userRole = ((r as any).userRole || (r as any).UserRole)?.toLowerCase() || r.role?.toLowerCase();
        if (!map.has(key)) {
          map.set(key, {
            ...r,
            isOwner: userRole === "owner",
            isMember: userRole === "member",
          });
        } else {
          const existing = map.get(key)!;
          if (userRole === "owner") existing.isOwner = true;
          if (userRole === "member") existing.isMember = true;

          if (r.primaryOwner && !existing.primaryOwner)
            existing.primaryOwner = r.primaryOwner;
          if (r.secondaryOwner && !existing.secondaryOwner)
            existing.secondaryOwner = r.secondaryOwner;
        }
      }

      const deduped = Array.from(map.values());
      setResources(deduped);

    } catch (err: any) {
      console.error("Failed to load resources", err);
      setError(err.message ?? "Failed to load resources");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadResources();
  }, [accounts]);

  // ---- Load Compliance Data ----
  // ---- Handle graphId parameter from URL (direct group access) ----
  useEffect(() => {
    if (!graphId) return;

    // Make tab creation idempotent (important in React StrictMode / rapid re-renders).
    setOpenTabs((prev) => {
      if (prev.some((t) => t.id === graphId)) return prev;
      return [...prev, { id: graphId, title: "Loading group...", isGraphId: true }];
    });
    setActiveTabId(graphId);

    const loadGraphGroup = async () => {
      try {
        setLoading(true);
        const groupData = await getGroupByGraphId(graphId);
        setOpenTabs((prev) =>
          prev.map((t) =>
            t.id === graphId
              ? { ...t, title: groupData.displayName || "Group", isGraphId: true }
              : t
          )
        );
      } catch (err: any) {
        console.error("Failed to load graph group:", err);
        showError("Failed to Load Group", "Could not load group from Microsoft Graph. Please check the group ID.");
      } finally {
        setLoading(false);
      }
    };

    void loadGraphGroup();
  }, [graphId]);

  // ---- Filtering ----

  const parseMetadata = (metadataJson: string | null | undefined): ResourceMetadata => {
    try {
      if (!metadataJson) return {};
      return JSON.parse(metadataJson) as ResourceMetadata;
    } catch {
      return {};
    }
  };

  const hasMissingFields = (resource: ResourceSummary): boolean => {
    const metadata = parseMetadata(resource.metadataJson);
    const primaryOwner = metadata.primaryOwnerNtid || resource.primaryOwner;
    const secondaryOwner = metadata.secondaryOwnerNtid || resource.secondaryOwner;
    const businessUnit = metadata.businessUnit;
    const costCenter = metadata.costCenter;

    if (!primaryOwner || !secondaryOwner || !businessUnit || !costCenter) {
      return true;
    }

    if (resource.metadataNextReviewDt) {
      const nextReviewDate = new Date(resource.metadataNextReviewDt);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      if (nextReviewDate < today) {
        return true;
      }
    }

    return false;
  };

  const filtered = useMemo(() => {
    return resources
      .filter((r) => {
        if (ownershipFilter === "owned" && r.isOwner !== true) {
          return false;
        }
        if (ownershipFilter === "member" && r.isMember !== true) {
          return false;
        }

        const hasMissingInformation = hasMissingFields(r);
        if (metadataStatusFilter === "needs-information" && !hasMissingInformation) {
          return false;
        }
        if (metadataStatusFilter === "up-to-date" && hasMissingInformation) {
          return false;
        }

        if (!search) return true;

        const q = search.toLowerCase();
        return (
          (r.displayName ?? "").toLowerCase().includes(q) ||
          (r.url ?? "").toLowerCase().includes(q) ||
          (r.primaryOwner ?? "").toLowerCase().includes(q) ||
          (r.secondaryOwner ?? "").toLowerCase().includes(q)
        );
      })
      .sort((a, b) =>
        (a.displayName ?? "").localeCompare(b.displayName ?? "")
      );
  }, [resources, search, ownershipFilter, metadataStatusFilter]);

  // ---- Helpers ----

  const patchResourceMetadata = (resource: ResourceSummary, updates: ResourceMetadata): ResourceSummary => {
    const existingMetadata = parseMetadata(resource.metadataJson);
    const mergedMetadata = { ...existingMetadata, ...updates };
    const reviewedAt = new Date();
    const nextReview = new Date(reviewedAt.getTime() + 365 * 24 * 60 * 60 * 1000);

    return {
      ...resource,
      metadataJson: JSON.stringify(mergedMetadata),
      primaryOwner: mergedMetadata.primaryOwnerNtid || resource.primaryOwner,
      secondaryOwner: mergedMetadata.secondaryOwnerNtid || resource.secondaryOwner,
      metadataLastReviewedBy: "current-user",
      metadataLastReviewedDt: reviewedAt.toISOString(),
      metadataNextReviewDt: nextReview.toISOString(),
    };
  };

  const handleResourceMetadataUpdated = (
    target: { internalResourceId?: string | null; groupId?: string | null },
    updates: ResourceMetadata
  ) => {
    setResources((prev) =>
      prev.map((resource) => {
        const matchesInternalId = target.internalResourceId && resource.internalResourceId === target.internalResourceId;
        const matchesGroupId = target.groupId && resource.groupId === target.groupId;

        return matchesInternalId || matchesGroupId
          ? patchResourceMetadata(resource, updates)
          : resource;
      })
    );
  };

  const getMetadataInitialData = (resource: ResourceSummary | null): ResourceMetadata | null => {
    if (!resource) return null;

    const metadata = parseMetadata(resource.metadataJson);
    return {
      ...metadata,
      primaryOwnerNtid: metadata.primaryOwnerNtid || resource.primaryOwner || "",
      secondaryOwnerNtid: metadata.secondaryOwnerNtid || resource.secondaryOwner || "",
      primaryAdminNtid: metadata.primaryAdminNtid || "",
      secondaryAdminNtid: metadata.secondaryAdminNtid || "",
      businessUnit: metadata.businessUnit || "",
      costCenter: metadata.costCenter || "",
      codeOfConductSensitive: Boolean(metadata.codeOfConductSensitive),
    };
  };

  const openMetadataEditor = (resource: ResourceSummary, event?: React.MouseEvent) => {
    event?.stopPropagation();
    setMetadataResource(resource);
  };

  const handleSaveListMetadata = async (updates: ResourceMetadata) => {
    if (!metadataResource?.groupId) {
      throw new Error("Unable to save metadata because this group is missing its Microsoft 365 group ID.");
    }

    await updateSiteSavedDataByGraphId(metadataResource.groupId, updates);
    handleResourceMetadataUpdated(
      {
        internalResourceId: metadataResource.internalResourceId,
        groupId: metadataResource.groupId,
      },
      updates
    );
  };

  const getBUBadgeClass = (bu: string | undefined): string => {
    switch (bu) {
      case "ENMAX Corporation":
        return "resource-bu-badge-corporation";
      case "ENMAX Energy":
        return "resource-bu-badge-energy";
      case "ENMAX Power":
        return "resource-bu-badge-power";
      default:
        return "resource-bu-badge-default";
    }
  };

  const resourcesWithMissingFields = useMemo(() => {
    return filtered.filter((r) => hasMissingFields(r));
  }, [filtered]);

  // Filter to only owned groups (for metadata updates)
  const ownedGroups = filtered.filter(r => r.isOwner === true);
  const ownedWithMissing = resourcesWithMissingFields.filter(r => r.isOwner === true);

  // Determine which resources should be selectable
  // Only owners can update; if owned groups have missing metadata, only those are selectable
  const selectableResources = ownedWithMissing.length > 0
    ? ownedWithMissing
    : ownedGroups;

  const isAtMaxSelections = selectedResourceIds.size >= MAX_BULK_SELECTIONS;

  const hasActiveFilters = search.trim() !== "" || ownershipFilter !== "all" || metadataStatusFilter !== "all";

  const clearFilters = () => {
    setSearch("");
    setOwnershipFilter("all");
    setMetadataStatusFilter("all");
  };

  const showGroupsMissingInformation = () => {
    setOwnershipFilter("owned");
    setMetadataStatusFilter("needs-information");
  };

  const getUserEmail = (user?: { mail?: string; userPrincipalName?: string } | null) =>
    user?.mail || user?.userPrincipalName || "";

  const openCreateGroupModal = async () => {
    setShowCreateGroupModal(true);
    setCreateGroupError(null);
    setCreateGroupForm({
      title: "",
      description: "",
      primaryOwnerEmail: "",
      secondaryOwnerEmail: "",
      businessUnit: "",
      costCenter: "",
    });

    try {
      setLoadingCreateDefaults(true);
      const defaults = await getGroupCreateDefaults();
      setCreateGroupForm((current) => ({
        ...current,
        primaryOwnerEmail: getUserEmail(defaults.primaryOwner),
        secondaryOwnerEmail: getUserEmail(defaults.secondaryOwner),
        costCenter: defaults.costCenter || defaults.primaryOwner?.employeeDeptId || "",
      }));
    } catch (err: any) {
      setCreateGroupError(err?.response?.data?.message || err?.response?.data?.error || err?.message || "Unable to load your default group details.");
    } finally {
      setLoadingCreateDefaults(false);
    }
  };

  const updateCreateGroupForm = (field: keyof typeof createGroupForm, value: string) => {
    setCreateGroupForm((current) => ({ ...current, [field]: value }));
    if (createGroupError) setCreateGroupError(null);
  };

  const handleCreateGroup = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!createGroupForm.title.trim()) {
      setCreateGroupError("Group title is required.");
      return;
    }
    if (!createGroupForm.primaryOwnerEmail.trim()) {
      setCreateGroupError("Primary owner is required.");
      return;
    }

    try {
      setCreatingGroup(true);
      const created = await createGroup({
        title: createGroupForm.title.trim(),
        description: createGroupForm.description.trim() || undefined,
        primaryOwnerEmail: createGroupForm.primaryOwnerEmail.trim(),
        secondaryOwnerEmail: createGroupForm.secondaryOwnerEmail.trim() || undefined,
        businessUnit: createGroupForm.businessUnit.trim() || undefined,
        costCenter: createGroupForm.costCenter.trim() || undefined,
      });

      setShowCreateGroupModal(false);
      showSuccess("Group Created", `Created ${created.displayName}. It may take a few minutes for all Microsoft 365 services to finish provisioning.`);
      await loadResources();
    } catch (err: any) {
      setCreateGroupError(err?.response?.data?.message || err?.response?.data?.error || err?.message || "Failed to create group.");
    } finally {
      setCreatingGroup(false);
    }
  };

  const handleSelectResource = (resourceId: string) => {
    setSelectedResourceIds((prev) => {
      const next = new Set(prev);
      if (next.has(resourceId)) {
        // Always allow deselecting
        next.delete(resourceId);
      } else if (next.size < MAX_BULK_SELECTIONS) {
        // Only add if under limit
        next.add(resourceId);
      }
      return next;
    });
  };

  const handleSelectAll = () => {
    if (selectedResourceIds.size === selectableResources.length) {
      setSelectedResourceIds(new Set());
    } else {
      // Select up to max limit
      const toSelect = selectableResources
        .slice(0, MAX_BULK_SELECTIONS)
        .map((r) => r.internalResourceId);
      setSelectedResourceIds(new Set(toSelect));
    }
  };

  const handleBulkUpdate = async (updates: { 
    primaryOwnerNtid?: string;
    secondaryOwnerNtid?: string;
    costCenter?: string;
    businessUnit?: string;
  }) => {
    try {
      setUpdatingMetadata(true);
      const token = await acquireTokenForApi();
      const { confirmMetadataReview } = await import("@/services/resourcesApi");
      
      for (const resourceId of selectedResourceIds) {
        // Find the resource to get its groupId
        const resource = resources.find(r => r.internalResourceId === resourceId);
        if (!resource?.groupId) {
          console.warn(`No groupId found for resource ${resourceId}`);
          continue;
        }

        // Update metadata
        const metadataJson = JSON.stringify(updates);
        const response = await fetch(
          `${import.meta.env.VITE_API_URL}/api/resources/${resourceId}`,
          {
            method: "PUT",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({ metadataJson }),
          }
        );

        if (!response.ok) {
          throw new Error(`Failed to update resource ${resourceId}`);
        }

        // Confirm metadata review (updates SharePointSiteCollections)
        await confirmMetadataReview(resource.groupId);
      }

      // Update local state with new metadata instead of full reload
      setResources((prev) =>
        prev.map((r) => {
          if (selectedResourceIds.has(r.internalResourceId)) {
            return patchResourceMetadata(r, updates);
          }
          return r;
        })
      );

      // Clear selection and close modal
      setSelectedResourceIds(new Set());
      setShowBulkUpdateModal(false);

      showSuccess("Success", `Updated metadata for ${selectedResourceIds.size} resource(s)`);
    } catch (err: any) {
      console.error("Bulk update failed", err);
      showError("Update Failed", err.message ?? "Failed to update resources");
    } finally {
      setUpdatingMetadata(false);
    }
  };

  // ---- Handle clicking a resource row ----

  const openResourceTab = (res: ResourceSummary) => {
    // Prefer Graph-backed details for Groups to avoid stale internal IDs returning 404.
    const isGroup = (res.resourceType || "").toLowerCase() === "group";
    const id = isGroup && res.groupId ? res.groupId : res.internalResourceId;
    const isGraphId = Boolean(isGroup && res.groupId);

    setOpenTabs((prev) => {
      if (prev.some((t) => t.id === id)) return prev;
      return [...prev, { id, title: res.displayName || id, isGraphId }];
    });

    setActiveTabId(id);
    setLoadingTabId(id); // Show loading animation
    
    // Clear loading state after content starts rendering
    const timeout = setTimeout(() => setLoadingTabId(null), 500);
    return () => clearTimeout(timeout);
  };

  // ---- Handle closing a tab ----

  const closeTab = (tabId: string, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent tab selection

    // Don't close the "resources" tab
    if (tabId === "resources") return;

    // Remove the tab from openTabs
    setOpenTabs((prev) => prev.filter((t) => t.id !== tabId));

    // If closing the active tab, switch to "resources" tab
    if (activeTabId === tabId) {
      setActiveTabId("resources");
    }
  };

  // ---- Handle removing self from group ----

  const handleRemoveClick = (res: ResourceSummary, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent row click
    setGroupToRemove(res);
    setShowConfirmRemove(true);
  };

  const handleLeaveGroup = async () => {
    if (!groupToRemove) return;

    try {
      setRemovingGroupId(groupToRemove.internalResourceId);
      const token = await acquireTokenForApi();
      const graphGroupId = groupToRemove.groupId;
      if (!graphGroupId) {
        throw new Error("This resource does not have a Microsoft 365 group ID.");
      }

      const res = await fetch(
        `${import.meta.env.VITE_API_URL}/api/resources/graph/${encodeURIComponent(graphGroupId)}/members/me`,
        { method: "DELETE", headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok) throw new Error(await res.text());
      
      // Remove from list
      setResources((prev) => prev.filter((r) => r.groupId !== graphGroupId));
      
      setShowConfirmRemove(false);
      setGroupToRemove(null);
    } catch (err) {
      console.error(err);
      const message = err instanceof Error && err.message.trim()
        ? err.message
        : "We couldn't remove you from this group. Please try again later or check your permissions.";
      showError("Failed to Leave Group", message);
    } finally {
      setRemovingGroupId(null);
    }
  };

  const handleDeleteGroupRequest = (res: ResourceSummary, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!res.isOwner) return;
    setGroupToDelete(res);
    setShowConfirmDelete(true);
  };

  const handleDeleteGroup = async () => {
    if (!groupToDelete) return;

    try {
      setDeletingGroupId(groupToDelete.internalResourceId);
      const token = await acquireTokenForApi();
      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/api/resources/${groupToDelete.internalResourceId}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || "Failed to delete group");
      }

      setResources((prev) => prev.filter((r) => r.internalResourceId !== groupToDelete.internalResourceId));
      setSelectedResourceIds((prev) => {
        const next = new Set(prev);
        next.delete(groupToDelete.internalResourceId);
        return next;
      });
      setShowConfirmDelete(false);
      setGroupToDelete(null);

      showSuccess("Group Deleted", `The group "${groupToDelete.displayName}" was removed successfully.`);
    } catch (err) {
      console.error(err);
      const message = err instanceof Error && err.message.trim()
        ? err.message
        : "We couldn't delete this group. Please try again later or check your permissions.";
      showError("Failed to Delete Group", message);
    } finally {
      setDeletingGroupId(null);
    }
  };


  return (
    <div className="m365-wrapper resources-page container-fluid">

      {/* Top Title */}
      <div className="resources-page-intro mb-3">
        <div className="resources-page-intro-header d-flex justify-content-between align-items-start gap-3 flex-wrap">
          <div>
            <h4 className="page-title mb-1">My Groups</h4>
            <p className="text-muted mb-0">View and manage the Microsoft 365 groups you own.</p>
          </div>
          <Button variant="primary" size="sm" className="resources-create-group-btn" onClick={openCreateGroupModal}>
            <FaPlus className="me-2" />
            Create group
          </Button>
        </div>

        <div className="resources-page-guidance" aria-label="Group cleanup guidance">
          <div className="resources-guidance-step">
            <span className="resources-guidance-icon"><FaTrash /></span>
            <div className="resources-guidance-copy">
              <strong>1. Delete</strong>
              <span>Delete groups you no longer need.</span>
            </div>
          </div>
          <div className="resources-guidance-step">
            <span className="resources-guidance-icon"><FaUserMinus /></span>
            <div className="resources-guidance-copy">
              <strong>2. Leave</strong>
              <span>Leave groups you no longer need to be part of.</span>
            </div>
          </div>
          <div className="resources-guidance-step">
            <span className="resources-guidance-icon"><FaPen /></span>
            <div className="resources-guidance-copy">
              <strong>3. Update</strong>
              <span>Update missing information where you can. <em>It doesn't need to be perfect.</em></span>
            </div>
          </div>
        </div>
      </div>


      {/* Tabs */}
      <div className="resources-top-tabs d-flex flex-wrap">
        {openTabs.map((tab) => (
          <div
            key={tab.id}
            className={
              "resources-tab" + (activeTabId === tab.id ? " active" : "")
            }
            onClick={() => setActiveTabId(tab.id)}
          >
            {loadingTabId === tab.id && (
              <Spinner
                size="sm"
                animation="border"
                className="me-2"
                style={{ width: '14px', height: '14px' }}
              />
            )}
            <span className="tab-title">{tab.title}</span>
            {tab.id !== "resources" && (
              <button
                className="tab-close-btn"
                onClick={(e) => closeTab(tab.id, e)}
                title="Close tab"
              >
                <FaTimes />
              </button>
            )}
          </div>
        ))}
      </div>


      {/* Content Wrapper */}
      <div className="tabContentWrapper">

        {/* RESOURCES LIST TAB CONTENT */}
        {activeTabId === "resources" && (
          <div className="resourcesTabContent">

            {/* Search + filters */}
            <div className="resources-header-actions d-flex align-items-center gap-3 mb-3 flex-wrap">
              <div className="resources-filter-field resources-filter-field-search">
                <label className="form-label small text-muted mb-1" htmlFor="groups-search-filter">
                  Search
                </label>
                <input
                  id="groups-search-filter"
                  type="text"
                  className="form-control form-control-sm"
                  placeholder="Search by name, URL, owner…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>

              <div className="resources-filter-field">
                <label className="form-label small text-muted mb-1" htmlFor="groups-access-filter">
                  Relationship
                </label>
                <select
                  id="groups-access-filter"
                  className="form-select form-select-sm"
                  value={ownershipFilter}
                  onChange={(e) => setOwnershipFilter(e.target.value as OwnershipFilter)}
                  aria-label="Filter groups by your access"
                >
                  <option value="all">All</option>
                  <option value="owned">Groups I own</option>
                  <option value="member">Groups I belong to</option>
                </select>
              </div>

              <div className="resources-filter-field">
                <label className="form-label small text-muted mb-1" htmlFor="groups-status-filter">
                  Health
                </label>
                <select
                  id="groups-status-filter"
                  className="form-select form-select-sm"
                  value={metadataStatusFilter}
                  onChange={(e) => setMetadataStatusFilter(e.target.value as MetadataStatusFilter)}
                  aria-label="Filter groups by metadata status"
                >
                  <option value="all">All</option>
                  <option value="up-to-date">Healthy</option>
                  <option value="needs-information">Unhealthy</option>
                </select>
              </div>

              <Button
                variant="link"
                size="sm"
                className="resources-clear-filters"
                onClick={clearFilters}
                disabled={!hasActiveFilters}
              >
                <FaSyncAlt className="me-2" />
                Clear filters
              </Button>

{/*
              <div className="form-check form-switch mb-0">
                <input
                  id="toggle-onedrive"
                  className="form-check-input"
                  type="checkbox"
                  checked={showOneDrive}
                  onChange={(e) => setShowOneDrive(e.target.checked)}
                />
                <label
                  className="form-check-label small"
                  htmlFor="toggle-onedrive"
                >
                  Show OneDrive
                </label>
              </div>
*/}
            </div>

            {/* Bulk update button (only shown if there are selected resources) */}
            {selectedResourceIds.size > 0 && (
              <div className="mb-3 d-flex gap-2">
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => setShowBulkUpdateModal(true)}
                  disabled={updatingMetadata}
                >
                  Update Info ({selectedResourceIds.size} selected)
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setSelectedResourceIds(new Set())}
                  disabled={updatingMetadata}
                >
                  Clear Selection
                </Button>
              </div>
            )}

            {/* Warning for missing data */}
            {ownedWithMissing.length > 0 && (
              <Alert variant="warning" className="mb-3 d-flex align-items-center gap-2">
                <FaExclamationTriangle style={{ fontSize: "1.25rem", flexShrink: 0 }} />
                <div style={{ flex: 1 }}>
                  <strong>{ownedWithMissing.length} group{ownedWithMissing.length !== 1 ? "s" : ""} need additional information.</strong>
                  <p className="mb-0 small mt-1">
                    Select the groups below (max 10 at a time) and click "Update Info" to add missing metadata.
                  </p>
                </div>
                <Button variant="outline-warning" size="sm" onClick={showGroupsMissingInformation}>
                  Show only these
                </Button>
              </Alert>
            )}

            {/* Warning for max selections */}
            {isAtMaxSelections && (
              <Alert variant="info" className="mb-3 d-flex align-items-center gap-2">
                <span style={{ flexShrink: 0 }}>ℹ️</span>
                <div>
                  Maximum {MAX_BULK_SELECTIONS} resources can be updated at a time. Deselect some to select others.
                </div>
              </Alert>
            )}

            {/* Resources Table */}
            <div className="resources-list-card">
              {loading && (
                <div className="text-center py-5">
                  <Spinner
                    animation="border"
                    role="status"
                    variant="primary"
                    style={{ width: "4rem", height: "4rem", borderWidth: "0.4rem" }}
                  />
                </div>
              )}

              {error && !loading && (
                <div className="p-3 text-danger">Error: {error}</div>
              )}

              {!loading && !error && (
                <table className="table table-hover resources-table mb-0">
                  <thead>
                    <tr>
                      {selectableResources.length > 0 && (
                        <th style={{ width: "4%" }} className="text-center">
                          <input
                            type="checkbox"
                            className="form-check-input"
                            checked={selectedResourceIds.size === selectableResources.length && selectableResources.length > 0}
                            ref={(el) => {
                              if (el) {
                                el.indeterminate = selectedResourceIds.size > 0 && selectedResourceIds.size < selectableResources.length;
                              }
                            }}
                            onChange={handleSelectAll}
                            title="Select all"
                          />
                        </th>
                      )}
                      <th style={{ width: "34%" }}>Name</th>
                      <th style={{ width: "10%" }}>Type</th>
                      <th style={{ width: "14%" }}>Owners</th>
                      <th style={{ width: "14%" }}>BU / Cost Center</th>
                      <th style={{ width: "12%" }}>Status</th>
                      <th style={{ width: "12%" }} className="text-center">Quick actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((res) => {

                      const icon = getResourceTypeIcon(res.resourceType);
                      const isGroup = res.resourceType?.toLowerCase() === "group";
                      const needsInfo = hasMissingFields(res);

                      return (
                        <tr
                          key={res.internalResourceId}
                          className="resource-row"
                          onClick={() => openResourceTab(res)}
                        >
                          {selectableResources.length > 0 && selectableResources.some((r) => r.internalResourceId === res.internalResourceId) && (
                            <td className="text-center" onClick={(e) => e.stopPropagation()}>
                              <input
                                type="checkbox"
                                className="form-check-input"
                                checked={selectedResourceIds.has(res.internalResourceId)}
                                onChange={() => handleSelectResource(res.internalResourceId)}
                                disabled={isAtMaxSelections && !selectedResourceIds.has(res.internalResourceId)}
                              />
                            </td>
                          )}
                          {selectableResources.length > 0 && !selectableResources.some((r) => r.internalResourceId === res.internalResourceId) && (
                            <td />
                          )}
                          <td>
                            <div className="d-flex align-items-start gap-2">
                              <div className="resource-thumbnail">
                                <span className="resource-thumbnail-icon">
                                  {icon}
                                </span>
                              </div>

                              <div className="d-flex flex-column flex-grow-1">
                                <span className="fw-semibold">
                                  {res.displayName ?? "(no name)"}
                                  {res.isOwner && (
                                    <span className="badge bg-light text-dark border ms-2 resource-owner-badge" title="You own this group">
                                      Owner
                                    </span>
                                  )}
                                </span>
                                <span className="resource-subtext text-muted small">
                                  {res.groupId ? `Group ID: ${res.groupId}` : ""}
                                </span>
                              </div>
                            </div>
                          </td>

                          <td>
                            <span className="badge bg-light text-dark border resource-type-pill">
                              {res.resourceType || "Unknown"}
                            </span>
                          </td>

                          <td onClick={(event) => openMetadataEditor(res, event)} className="resource-editable-cell" title="Edit group information">
                            {(() => {
                              const metadata = parseMetadata(res.metadataJson);
                              const primaryOwner = metadata.primaryOwnerNtid || res.primaryOwner;
                              const secondaryOwner = metadata.secondaryOwnerNtid || res.secondaryOwner;
                              
                              if (!primaryOwner && !secondaryOwner) {
                                return <span className="text-muted small">-</span>;
                              }
                              
                              return (
                                <div className="d-flex flex-column gap-1 small">
                                  {primaryOwner && (
                                    <div>{primaryOwner}</div>
                                  )}
                                  {secondaryOwner && (
                                    <div className="text-muted">{secondaryOwner}</div>
                                  )}
                                </div>
                              );
                            })()}
                          </td>

                          <td onClick={(event) => openMetadataEditor(res, event)} className="resource-editable-cell" title="Edit group information">
                            {(() => {
                              const metadata = parseMetadata(res.metadataJson);
                              const bu = metadata.businessUnit;
                              const cc = metadata.costCenter;
                              if (!bu && !cc) return <span className="text-muted small">-</span>;
                              
                              const buClass = getBUBadgeClass(bu);
                              
                              return (
                                <div className="d-flex flex-column gap-2 small">
                                  {bu && (
                                    <span className={`badge resource-bu-badge ${buClass}`}>
                                      {bu}
                                    </span>
                                  )}
                                  {cc && (
                                    <div className="text-muted">Dept ID: {cc}</div>
                                  )}
                                </div>
                              );
                            })()}
                          </td>

                          <td onClick={(event) => openMetadataEditor(res, event)} className="resource-editable-cell" title="Edit group information">
                            {needsInfo ? (
                              <span
                                className="badge resource-status-badge resource-status-badge-warning"
                                title={res.isOwner ? "You can update this information" : "Only group owners can update this information"}
                              >
                                <FaExclamationTriangle className="me-1" />
                                Missing info
                              </span>
                            ) : (
                              <span className="badge resource-status-badge resource-status-badge-complete">
                                <FaCheckCircle className="me-1" />
                                Complete
                              </span>
                            )}
                          </td>

                          <td className="text-center" onClick={(e) => e.stopPropagation()}>
                            {isGroup && (
                              <div className="d-flex align-items-center justify-content-center gap-2">
                                {res.url ? (
                                  <a
                                    href={res.url}
                                    target="_blank"
                                    rel="noreferrer"
                                    title={`Open site in new window - ${res.url}`}
                                    className="globe-icon globe-icon-active"
                                    style={{ fontSize: "1.1rem", cursor: "pointer" }}
                                  >
                                    <FaGlobe />
                                  </a>
                                ) : (
                                  <span
                                    title="Couldn't retrieve URL for this site"
                                    className="globe-icon globe-icon-disabled"
                                    style={{ fontSize: "1.1rem", cursor: "not-allowed" }}
                                  >
                                    <FaGlobe />
                                  </span>
                                )}
                                <ServiceIcons
                                  team={res.hasTeam && res.groupId ? { id: res.groupId, webUrl: `https://teams.microsoft.com/l/team/${res.groupId}` } as TeamInfo : null}
                                  plannerPlans={res.plannerPlans || null}
                                  siteUrl={res.siteUrl || null}
                                />
                                <FaUserMinus
                                  className={`text-danger ${removingGroupId === res.internalResourceId ? "disabled" : "cursor-pointer"}`}
                                  style={{ fontSize: "20px" }}
                                  onClick={(e) => !removingGroupId && handleRemoveClick(res, e)}
                                  title={removingGroupId === res.internalResourceId ? "Leaving group..." : "Leave group"}
                                />
                                {res.isOwner && (
                                  <FaTrash
                                    className={`text-danger ${deletingGroupId === res.internalResourceId ? "disabled" : "cursor-pointer"}`}
                                    style={{ fontSize: "18px" }}
                                    onClick={(e) => handleDeleteGroupRequest(res, e)}
                                    title={deletingGroupId === res.internalResourceId ? "Deleting group..." : "Delete group"}
                                  />
                                )}
                              </div>
                            )}
                            {!isGroup && <span className="text-muted">-</span>}
                          </td>
                        </tr>
                      );
                    })}

                    {!loading && !error && filtered.length === 0 && (
                      <tr>
                        <td colSpan={selectableResources.length > 0 ? 7 : 6} className="text-muted text-center py-3">
                          No resources found. Try adjusting your filters.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        )}


        {/* DETAIL TABS CONTENT */}
        {openTabs
          .filter((t) => t.id !== "resources")
          .map((tab) => (
            <div
              key={tab.id}
              className={
                "tabDetailContent" +
                (activeTabId === tab.id ? " active" : " d-none")
              }
            >
              <ResourceDetailsPanel 
                internalResourceId={tab.id}
                isGraphId={tab.isGraphId}
                resourceSnapshot={resources.find((resource) =>
                  tab.isGraphId
                    ? resource.groupId === tab.id
                    : resource.internalResourceId === tab.id
                )}
                onMetadataSaved={handleResourceMetadataUpdated}
              />
            </div>
          ))}
      </div>

      {/* Confirmation and Error Modals */}
      <BulkUpdateMetadataModal
        show={showBulkUpdateModal}
        resources={selectableResources.filter((r) =>
          selectedResourceIds.has(r.internalResourceId)
        )}
        onUpdate={handleBulkUpdate}
        onClose={() => setShowBulkUpdateModal(false)}
        loading={updatingMetadata}
      />
      <SiteSavedDataModal
        show={metadataResource !== null}
        onHide={() => setMetadataResource(null)}
        onSave={handleSaveListMetadata}
        initialData={getMetadataInitialData(metadataResource)}
        groupId={metadataResource?.groupId || null}
      />
      <Modal show={showCreateGroupModal} onHide={() => !creatingGroup && setShowCreateGroupModal(false)} centered>
        <Form onSubmit={handleCreateGroup}>
          <Modal.Header closeButton={!creatingGroup}>
            <Modal.Title>Create group</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {createGroupError && <Alert variant="danger">{createGroupError}</Alert>}
            {loadingCreateDefaults && (
              <div className="d-flex align-items-center gap-2 text-muted mb-3">
                <Spinner animation="border" size="sm" />
                Loading your defaults...
              </div>
            )}
            <Form.Group className="mb-3" controlId="create-group-title">
              <Form.Label>Title</Form.Label>
              <Form.Control
                value={createGroupForm.title}
                onChange={(event) => updateCreateGroupForm("title", event.target.value)}
                placeholder="Group name"
                maxLength={256}
                required
                disabled={creatingGroup}
              />
            </Form.Group>
            <Form.Group className="mb-3" controlId="create-group-description">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={createGroupForm.description}
                onChange={(event) => updateCreateGroupForm("description", event.target.value)}
                placeholder="What this group is for"
                maxLength={1024}
                disabled={creatingGroup}
              />
            </Form.Group>
            <div className="row g-3">
              <Form.Group className="col-md-6" controlId="create-group-primary-owner">
                <Form.Label>Owner</Form.Label>
                <Form.Control
                  type="email"
                  value={createGroupForm.primaryOwnerEmail}
                  onChange={(event) => updateCreateGroupForm("primaryOwnerEmail", event.target.value)}
                  required
                  disabled={creatingGroup || loadingCreateDefaults}
                />
              </Form.Group>
              <Form.Group className="col-md-6" controlId="create-group-secondary-owner">
                <Form.Label>Additional owner</Form.Label>
                <Form.Control
                  type="email"
                  value={createGroupForm.secondaryOwnerEmail}
                  onChange={(event) => updateCreateGroupForm("secondaryOwnerEmail", event.target.value)}
                  disabled={creatingGroup || loadingCreateDefaults}
                />
              </Form.Group>
              <Form.Group className="col-md-6" controlId="create-group-business-unit">
                <Form.Label>Business unit</Form.Label>
                <Form.Select
                  value={createGroupForm.businessUnit}
                  onChange={(event) => updateCreateGroupForm("businessUnit", event.target.value)}
                  disabled={creatingGroup || loadingCreateDefaults}
                >
                  <option value="">Select business unit</option>
                  <option value="ENMAX Corporation">ENMAX Corporation</option>
                  <option value="ENMAX Energy">ENMAX Energy</option>
                  <option value="ENMAX Power">ENMAX Power</option>
                </Form.Select>
              </Form.Group>
              <Form.Group className="col-md-6" controlId="create-group-cost-center">
                <Form.Label>Cost center</Form.Label>
                <Form.Control
                  value={createGroupForm.costCenter}
                  onChange={(event) => updateCreateGroupForm("costCenter", event.target.value)}
                  disabled={creatingGroup || loadingCreateDefaults}
                />
              </Form.Group>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowCreateGroupModal(false)} disabled={creatingGroup}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" disabled={creatingGroup || loadingCreateDefaults}>
              {creatingGroup ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Creating...
                </>
              ) : (
                "Create group"
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
      <ConfirmModal
        show={showConfirmRemove}
        title="Leave group?"
        message={`You will be removed from this group:\n${groupToRemove?.displayName || ""}`}
        onConfirm={handleLeaveGroup}
        onCancel={() => setShowConfirmRemove(false)}
      />
      <DeleteGroupModal
        show={showConfirmDelete}
        groupName={groupToDelete?.displayName || ""}
        onConfirm={handleDeleteGroup}
        onClose={() => {
          setShowConfirmDelete(false);
          setGroupToDelete(null);
        }}
      />
      <ErrorModal
        show={errorModal.show}
        title={errorModal.title}
        message={errorModal.message}
        onClose={() => setErrorModal({ ...errorModal, show: false })}
      />
      <SuccessModal
        show={successModal.show}
        title={successModal.title}
        message={successModal.message}
        onClose={() => setSuccessModal({ ...successModal, show: false })}
      />
    </div>
  );
};

export default ResourcesPage;